import React, { useContext, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { type RestOneUpLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

type Props = {
  card: RestOneUpLaCard;
  onClick?: () => void;
  handleLossCardAction?: (opts: { action: RestModalForwards }) => void;
};
const LossOneUpImage: React.FC<Props> = ({
  card,
  card: {
    id,
    requires: { image, title, forwards, imageStretch },
  },
  onClick,
  handleLossCardAction,
}) => {
  const theme = useTheme() as EmotionTheme;
  const { isPreview } = useContext(FunnelContext);
  const redemptionEnabled = !card.max_redemptions_reached;

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);

  const handleClick = isPreview
    ? onClick
    : forwards
      ? () => {
          if (redemptionEnabled) {
            handleLossCardAction?.({ action: forwards });
          }
        }
      : () => undefined;
  return (
    <LossGridItem
      styles={css`
        display: flex;
        flex-direction: column;
        padding-top: 0;
        padding-bottom: 15px;
        @media (max-width: 480px) {
          padding-top: 0;
          padding-bottom: 15px;
        }

        .bbk-loss-component-title {
          padding-top: 30px;
        }

        .la-one-up-image {
          img {
            width: 100%;
          }
        }

        &.no-title .la-one-up-image {
          padding-top: 15px;
        }

        &.image-stretch.no-title .la-one-up-image {
          padding-top: 0;
        }

        &.image-stretch .la-one-up-image {
          margin-left: -30px;
          margin-right: -30px;
          margin-bottom: -15px;
          @media (max-width: 480px) {
            margin-left: -15px;
            margin-right: -15px;
          }

          img {
            border-bottom-right-radius: ${theme.borderRadius}px;
            border-bottom-left-radius: ${theme.borderRadius}px;
          }
        }

        &.no-title.image-stretch .la-one-up-image {
          img {
            border-radius: ${theme.borderRadius}px;
          }
        }

        &.image-stretch .la-one-up-image,
        &.no-title .la-one-up-image {
          flex: 1;
          display: flex;
          align-items: center;
        }

        &.la-disabled {
          cursor: not-allowed;
        }
      `}
      classes={cs('loss-one-up', makeClassNameSafe(card.name), {
        'image-stretch': imageStretch,
        'no-title': !title,
        'la-disabled': !redemptionEnabled,
      })}
      onClick={handleClick}
      hasFullCardAction={!!forwards}
    >
      <span ref={titleRef}>
        {title && (
          <LossTitle>
            <CustomerHtml value={title} />
          </LossTitle>
        )}
      </span>
      <div className="la-one-up-image" ref={imageRef}>
        <img src={image} alt="HERO" />
      </div>
    </LossGridItem>
  );
};
export default LossOneUpImage;
